import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Minus, Plus, ArrowLeft, Truck, ShieldCheck, RotateCcw, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

function Stars({ value = 0, size = "w-5 h-5", interactive = false, onSelect }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type={interactive ? "button" : undefined} disabled={!interactive}
          onClick={interactive ? () => onSelect(n) : undefined}
          className={interactive ? "cursor-pointer" : "cursor-default"} data-testid={interactive ? `star-${n}` : undefined}>
          <Star className={`${size} ${n <= Math.round(value) ? "fill-brand-yellow text-ink" : "fill-none text-ink/30"}`} />
        </button>
      ))}
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [myRating, setMyRating] = useState(5);
  const [myComment, setMyComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadProduct = () => api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
  const loadReviews = () => api.get(`/products/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});

  useEffect(() => {
    setProduct(null); setReviews([]);
    loadProduct(); loadReviews();
  }, [id]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please sign in to leave a review"); navigate("/login"); return; }
    setSubmitting(true);
    try {
      await api.post(`/products/${id}/reviews`, { rating: myRating, comment: myComment });
      toast.success("Thanks for your review!");
      setMyComment(""); setMyRating(5);
      loadReviews(); loadProduct();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  if (notFound) return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="product-not-found">
      <p className="text-2xl font-black mb-4">Product not found</p>
      <Link to="/shop" className="text-brand font-bold">← Back to shop</Link>
    </div>
  );

  if (!product) return (
    <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12">
      <div className="aspect-square rounded-3xl bg-secondary animate-pulse border-2 border-ink" />
      <div className="space-y-4"><div className="h-10 bg-secondary rounded-xl animate-pulse" /><div className="h-32 bg-secondary rounded-xl animate-pulse" /></div>
    </div>
  );

  const discount = product.compare_at_price ? Math.round(100 - (product.price / product.compare_at_price) * 100) : 0;

  const handleAdd = () => { addItem(product, qty); toast.success(`Added ${qty} × ${product.title} to cart!`); };
  const handleBuyNow = () => { addItem(product, qty); navigate("/cart"); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" data-testid="product-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 font-bold text-sm mb-6 hover:text-brand transition-colors" data-testid="back-button">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
          className="relative rounded-3xl border-2 border-ink shadow-hard-lg overflow-hidden aspect-square bg-secondary">
          <img src={product.image} alt={product.title} className="w-full h-full object-cover" />
          {discount > 0 && (
            <span className="absolute top-4 left-4 bg-brand-pink text-white rounded-full px-4 py-1.5 text-sm font-black uppercase border-2 border-ink shadow-hard-sm">-{discount}% OFF</span>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
          <p className="text-sm font-bold uppercase tracking-wider text-brand mb-2">{product.category}</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">{product.title}</h1>
          <div className="flex items-center gap-2 mb-6" data-testid="detail-rating">
            <Stars value={product.avg_rating || 0} />
            <span className="text-sm font-bold text-muted-foreground">
              {product.review_count ? `${product.avg_rating} · ${product.review_count} review${product.review_count > 1 ? "s" : ""}` : "No reviews yet"}
            </span>
          </div>
          <div className="flex items-baseline gap-3 mb-6">
            <span className="text-4xl font-black" data-testid="detail-price">${product.price.toFixed(2)}</span>
            {product.compare_at_price && <span className="text-xl text-muted-foreground line-through">${product.compare_at_price.toFixed(2)}</span>}
          </div>
          <p className="text-muted-foreground leading-relaxed mb-8">{product.description}</p>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center border-2 border-ink rounded-full overflow-hidden">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-11 h-11 flex items-center justify-center hover:bg-secondary" data-testid="qty-decrease"><Minus className="w-4 h-4" /></button>
              <span className="w-12 text-center font-black" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="w-11 h-11 flex items-center justify-center hover:bg-secondary" data-testid="qty-increase"><Plus className="w-4 h-4" /></button>
            </div>
            <span className="text-sm font-bold text-brand-green">In stock ({product.stock})</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <button onClick={handleAdd} className="flex-1 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold bg-white border-2 border-ink shadow-hard-btn hover:translate-y-[3px] hover:shadow-none transition-all" data-testid="add-to-cart-detail">
              <ShoppingBag className="w-5 h-5" /> Add to Cart
            </button>
            <button onClick={handleBuyNow} className="flex-1 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold bg-brand text-white border-2 border-ink shadow-hard-btn hover:translate-y-[3px] hover:shadow-none transition-all" data-testid="buy-now-button">
              Buy Now
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {[{ icon: Truck, t: "Free Shipping" }, { icon: ShieldCheck, t: "Secure Pay" }, { icon: RotateCcw, t: "Easy Returns" }].map((f, i) => (
              <div key={i} className="flex flex-col items-center gap-1 text-center bg-secondary rounded-2xl border-2 border-ink p-3">
                <f.icon className="w-5 h-5 text-brand" />
                <span className="text-xs font-bold">{f.t}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Reviews */}
      <section className="mt-16 max-w-3xl" data-testid="reviews-section">
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-6">Customer Reviews</h2>

        <div className="bg-white rounded-3xl border-2 border-ink shadow-hard p-6 mb-8">
          <h3 className="font-black mb-3">Write a review</h3>
          <form onSubmit={submitReview} className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="font-bold text-sm">Your rating:</span>
              <Stars value={myRating} interactive onSelect={setMyRating} size="w-7 h-7" />
            </div>
            <textarea value={myComment} onChange={(e) => setMyComment(e.target.value)} rows={3}
              placeholder="Share what you think about this product..."
              className="w-full bg-white border-2 border-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-brand/20 font-medium resize-none"
              data-testid="review-comment-input" />
            <button type="submit" disabled={submitting}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold bg-brand text-white border-2 border-ink shadow-hard-btn hover:translate-y-[3px] hover:shadow-none transition-all disabled:opacity-70"
              data-testid="submit-review-button">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Post Review"}
            </button>
          </form>
        </div>

        {reviews.length === 0 ? (
          <p className="text-muted-foreground" data-testid="no-reviews">Be the first to review this product!</p>
        ) : (
          <div className="space-y-4">
            {reviews.map((r) => (
              <div key={r.id} className="bg-white rounded-2xl border-2 border-ink shadow-hard-sm p-5" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-brand-yellow border-2 border-ink flex items-center justify-center font-black text-sm">
                      {r.user_name.charAt(0).toUpperCase()}
                    </div>
                    <span className="font-bold">{r.user_name}</span>
                  </div>
                  <Stars value={r.rating} size="w-4 h-4" />
                </div>
                {r.comment && <p className="text-muted-foreground text-sm leading-relaxed">{r.comment}</p>}
                <p className="text-xs text-muted-foreground mt-2">{new Date(r.created_at).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
